/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import pxToRem from "assets/theme/functions/pxToRem";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import FormControlErrorStyles from "assets/style/Component";
import FontComponent from "components/Responsive/fonts";
import CustomCheckbox from "components/CustomCheckbox/CustomCheckbox";
import Ellipsis from "components/Table/Ellipsis";
import Constants, { Colors } from "utils/Constants";

const SELECT_ALL_VALUE = "all";
const DEFAULT_OPTION_COLOR = "#D0D5DD";

const normalizeOptionColor = (color) => {
  if (typeof color !== "string") {
    return DEFAULT_OPTION_COLOR;
  }

  const trimmedColor = color.trim();
  if (!trimmedColor || trimmedColor.toLowerCase() === "default") {
    return DEFAULT_OPTION_COLOR;
  }

  const hexColor = trimmedColor.startsWith("#") ? trimmedColor : `#${trimmedColor}`;
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hexColor) ? hexColor : DEFAULT_OPTION_COLOR;
};

const normalizeOption = (option, index) => {
  if (!option) {
    return null;
  }

  const identifier =
    option[Constants.MONGOOSE_ID] !== undefined && option[Constants.MONGOOSE_ID] !== null
      ? option[Constants.MONGOOSE_ID].toString()
      : option.id !== undefined && option.id !== null
      ? option.id.toString()
      : option.value !== undefined && option.value !== null
      ? option.value.toString()
      : `option-${index}`;

  const titleCandidate =
    option.title || option.name || option.displayName || option.label || option.text || identifier;

  return {
    ...option,
    [Constants.MONGOOSE_ID]: identifier,
    title: titleCandidate ? titleCandidate.toString() : identifier,
  };
};

function SearchableMultiSelectDropdown({
  id,
  name,
  label,
  hint,
  helperText,
  menu,
  values,
  handleChange,
  error,
  disabled,
  marginBottom,
  labelStyle,
  valueStyle,
  showBadge,
  searchPlaceholder,
  enableSelectAll,
  dropdownIcon,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAllSelected, setIsAllSelected] = useState(false);
  const selectChipContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const fontSize = FontComponent({ sizes: {} });

  const DropdownIconComponent = dropdownIcon || KeyboardArrowDownIcon;

  const renderDropdownIcon = () => (
    <DropdownIconComponent fontSize="medium" sx={{ color: "#667085" }} />
  );

  const normalizedMenu = useMemo(() => {
    if (!Array.isArray(menu)) {
      return [];
    }

    const map = new Map();
    menu.forEach((option, index) => {
      const normalized = normalizeOption(option, index);
      if (normalized) {
        map.set(normalized[Constants.MONGOOSE_ID], normalized);
      }
    });
    return Array.from(map.values());
  }, [menu]);

  const normalizedValues = useMemo(() => {
    if (!Array.isArray(values)) {
      return [];
    }

    return values
      .map((value) => {
        if (value === undefined || value === null) {
          return "";
        }
        return value.toString();
      })
      .filter((value) => value !== "");
  }, [values]);

  const lowerCaseSearch = searchTerm.trim().replace(/\s+/g, " ").toLowerCase();

  const filteredMenu = useMemo(() => {
    if (!lowerCaseSearch) {
      return normalizedMenu;
    }

    return normalizedMenu.filter((option) => {
      const labelText = (option.title || "").replace(/\s+/g, " ");
      return labelText.toLowerCase().includes(lowerCaseSearch);
    });
  }, [normalizedMenu, lowerCaseSearch]);

  useEffect(() => {
    if (!enableSelectAll) {
      setIsAllSelected(false);
      return;
    }

    if (normalizedValues.length === 0) {
      setIsAllSelected(false);
      return;
    }

    const menuLength = normalizedMenu.length;
    const uniqueValues = new Set(normalizedValues);
    setIsAllSelected(menuLength > 0 && uniqueValues.size === menuLength);
  }, [enableSelectAll, normalizedMenu, normalizedValues]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
    setSearchTerm("");
    return () => {};
  }, [open]);

  const scrollToBottom = () => {
    if (selectChipContainerRef.current) {
      selectChipContainerRef.current.scrollTop = selectChipContainerRef.current.scrollHeight;
    }
  };

  const handleSelectAllToggle = () => {
    if (!enableSelectAll) {
      return;
    }

    if (isAllSelected) {
      handleChange(name, [], id);
      return;
    }

    const allValues = normalizedMenu.map((option) => option[Constants.MONGOOSE_ID]);
    handleChange(name, allValues, id);
  };

  const handleSelectChange = (event) => {
    const { value } = event.target;
    if (!Array.isArray(value)) {
      return;
    }

    const latestSelection = value[value.length - 1];

    if (latestSelection === SELECT_ALL_VALUE) {
      handleSelectAllToggle();
      return;
    }

    handleChange(name, value, id);
    setTimeout(scrollToBottom, 100);
  };

  const renderSelectedValues = (selected) => {
    if (!Array.isArray(selected) || selected.length === 0) {
      return (
        <MDTypography
          sx={{
            color: "#667085",
            fontSize,
            fontWeight: 400,
          }}
        >
          {hint}
        </MDTypography>
      );
    }

    if (showBadge) {
      const firstSelected = normalizedMenu.find(
        (option) => option[Constants.MONGOOSE_ID] === selected[0]
      );
      const remainingCount = selected.length - 1;

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {firstSelected && (
            <Chip
              label={
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Ellipsis title={firstSelected.title} maxWidth={valueStyle?.inputWidth || 200}>
                    {firstSelected.title}
                  </Ellipsis>
                </MDBox>
              }
            />
          )}
          {remainingCount > 0 && (
            <Chip
              label={`+${remainingCount}`}
              sx={{
                backgroundColor: "#EBEBEB",
                color: "#344767",
              }}
            />
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          maxHeight: valueStyle?.maxHeight || "150px",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
        ref={selectChipContainerRef}
      >
        {normalizedMenu
          .filter((option) => selected.includes(option[Constants.MONGOOSE_ID]))
          .map((option) => (
            <Chip
              key={option[Constants.MONGOOSE_ID]}
              label={
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Ellipsis
                    style={{ textTransform: "capitalize" }}
                    title={option.title}
                    maxWidth={valueStyle?.inputWidth || 200}
                  >
                    {option.title}
                  </Ellipsis>
                </MDBox>
              }
            />
          ))}
      </Box>
    );
  };

  return (
    <FormControl
      sx={{
        mr: 2,
        ml: 0,
        mt: pxToRem(8),
        width: "100%",
        marginBottom,
        ...FormControlErrorStyles,
      }}
      error={Boolean(error)}
      disabled={disabled}
      formhelpertextprops={{
        sx: { marginLeft: 0 },
      }}
    >
      {label && (
        <MDTypography
          variant="caption"
          mb={1}
          sx={{
            fontSize,
            fontWeight: labelStyle?.fontWeight || 500,
            color: labelStyle?.color || "#344054",
          }}
        >
          {label}
        </MDTypography>
      )}
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          backgroundColor: disabled ? Colors.DISABLED : "transparent",
        }}
      >
        <Select
          displayEmpty
          multiple
          onChange={handleSelectChange}
          IconComponent={renderDropdownIcon}
          labelId={id}
          id={id}
          name={name}
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          placeholder={hint}
          value={normalizedValues}
          sx={{
            color: "#667085",
            backgroundColor: disabled
              ? Colors.DISABLED
              : valueStyle?.backgroundColor || Colors.WHITE,
            paddingY: valueStyle?.paddingY || pxToRem(10),
            paddingX: valueStyle?.paddingX || pxToRem(12),
            fontSize: pxToRem(16),
            fontWeight: 400,
            width: "100%",
            height: valueStyle?.height || pxToRem(45),
            minHeight: valueStyle?.minHeight || valueStyle?.height || pxToRem(45),
            borderRadius: valueStyle?.borderRadius || pxToRem(8),
          }}
          MenuProps={{
            disableAutoFocusItem: true,
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            PopoverClasses: {
              root: "searchable-multi-select__popover",
            },
            PaperProps: {
              style: {
                width: pxToRem(valueStyle?.menuWidth || 405),
                maxHeight: valueStyle?.menuMaxHeight || 260,
                opacity: 1,
                transform: "none",
                border: "1px solid #D0D5DD",
                marginTop: valueStyle?.verticalMarginTop
                  ? valueStyle.verticalMarginTop
                  : pxToRem(8),
                marginLeft: pxToRem(-8),
              },
              sx: {
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
            },
            MenuListProps: {
              dense: true,
              sx: {
                paddingTop: 0,
                backgroundColor: Colors.WHITE,
                maxHeight: "inherit",
                overflowY: "auto",
                "& .MuiMenuItem-root": {
                  position: "relative",
                  zIndex: 1,
                },
              },
              subheader: (
                <ListSubheader
                  component="div"
                  sx={{
                    backgroundColor: Colors.WHITE,
                    display: "flex",
                    alignItems: "center",
                    paddingX: pxToRem(12),
                    paddingY: pxToRem(10),
                    borderBottom: "1px solid #F2F4F7",
                    position: "sticky",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    boxShadow: "0 2px 4px rgba(15, 23, 42, 0.04)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      backgroundColor: Colors.WHITE,
                      zIndex: -1,
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    inputRef={searchInputRef}
                    placeholder={searchTerm ? "" : searchPlaceholder}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: Colors.WHITE,
                        position: "relative",
                        zIndex: 1,
                        "& fieldset": {
                          borderColor: "#D0D5DD",
                        },
                        "&:hover fieldset": {
                          borderColor: "#D0D5DD",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#D0D5DD",
                          boxShadow: "0 0 0 2px rgba(13, 110, 253, 0.1)",
                        },
                      },
                      "& input": {
                        position: "relative",
                        zIndex: 2,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#98A2B3", fontSize: pxToRem(18) }} />
                        </InputAdornment>
                      ),
                      sx: {
                        fontSize: pxToRem(14),
                        paddingRight: pxToRem(4),
                        "& input::placeholder": {
                          color: "#98A2B3",
                          opacity: 1,
                        },
                      },
                      autoComplete: "off",
                    }}
                    size="small"
                  />
                </ListSubheader>
              ),
            },
            getContentAnchorEl: null,
          }}
          renderValue={renderSelectedValues}
        >
          {enableSelectAll && normalizedMenu.length > 0 && (
            <MenuItem
              value={SELECT_ALL_VALUE}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 0,
              }}
            >
              <CustomCheckbox checked={isAllSelected} />
              <MDTypography
                variant="subtitle1"
                sx={{
                  ml: 1,
                  textTransform: "capitalize",
                  fontSize: pxToRem(16),
                  fontWeight: 400,
                  color: "#667085",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {isAllSelected ? "Deselect All" : "Select All"}
              </MDTypography>
            </MenuItem>
          )}

          {filteredMenu.length > 0 ? (
            filteredMenu.map((option) => {
              const optionId = option[Constants.MONGOOSE_ID];
              const isChecked = normalizedValues.includes(optionId);
              const isDisabledOption = Boolean(option?.disabled);
              return (
                <MenuItem
                  key={optionId}
                  value={optionId}
                  sx={{ p: 0, opacity: isDisabledOption ? 0.7 : 1 }}
                  disabled={isDisabledOption}
                >
                  {!isDisabledOption && <CustomCheckbox name={optionId} checked={isChecked} />}
                  {option?.color && option?.color !== "" && !isDisabledOption && (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: normalizeOptionColor(option?.color),
                        borderRadius: "50%",
                        width: "10px",
                        height: "10px",
                        mr: 1,
                        ml: 1,
                        border: "1px solid #98A2B3",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <MDTypography
                    variant="subtitle1"
                    sx={{
                      ml: 1,
                      textTransform: "capitalize",
                      fontSize: pxToRem(16),
                      fontWeight: 400,
                      color: isDisabledOption ? "#98A2B3" : "#667085",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Ellipsis
                      maxWidth={pxToRem(Number(valueStyle?.menuWidth) - 50 || 360)}
                      title={option?.title}
                      style={{
                        textTransform: "capitalize",
                        fontSize,
                        color: isDisabledOption ? "#98A2B3" : "#667085",
                      }}
                    >
                      {option?.title}
                    </Ellipsis>
                  </MDTypography>
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled sx={{ justifyContent: "center" }}>
              <MDTypography
                variant="body2"
                sx={{
                  fontSize: pxToRem(14),
                  color: "#98A2B3",
                }}
              >
                No options found
              </MDTypography>
            </MenuItem>
          )}
        </Select>
      </MDBox>
      {helperText !== "" && <FormHelperText sx={{ marginLeft: 0 }}>{helperText}</FormHelperText>}
    </FormControl>
  );
}

SearchableMultiSelectDropdown.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  hint: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  menu: PropTypes.arrayOf(PropTypes.object),
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  marginBottom: PropTypes.string,
  labelStyle: PropTypes.objectOf(PropTypes.any),
  valueStyle: PropTypes.objectOf(PropTypes.any),
  showBadge: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  enableSelectAll: PropTypes.bool,
  dropdownIcon: PropTypes.elementType,
};

SearchableMultiSelectDropdown.defaultProps = {
  id: "",
  name: "",
  label: "",
  hint: "",
  error: false,
  helperText: "",
  menu: [],
  values: [],
  disabled: false,
  marginBottom: "0",
  labelStyle: {},
  valueStyle: {},
  showBadge: false,
  searchPlaceholder: "Search…",
  enableSelectAll: true,
  dropdownIcon: null,
};

export default SearchableMultiSelectDropdown;
